import { darken, SxProps, Theme } from "@mui/material";

import { getIconSize } from "../helpers/iconUtils";

import { ButtonSize, FilledButtonVibe, OtherButtonVibe } from "./types";

import { IconSize } from "@/types/mui";

export const ICON_SIZE_MAP: Record<ButtonSize, IconSize> = Object.freeze({
  small: "small",
  medium: "small",
  large: "medium",
});

export const getRootSx = (theme: Theme): SxProps<Theme> => {
  return {
    gap: 0.5,
    borderRadius: 1,
    transition: "background-color 200ms ease-out, outline 100ms ease-out",
    outline: "0px solid transparent",
    ["&:focus-visible"]: {
      outline: `3px solid ${theme.palette.border.brand.moderate}`,
    },
  };
};

export const getSizeSx = (theme: Theme, size: ButtonSize): SxProps<Theme> => {
  return {
    ...(size === "small" && {
      minHeight: theme.button.size.default.small,
      padding: theme.spacing(0.5, 2),
      ...theme.typography.button2,
      lineHeight: `${getIconSize(theme, ICON_SIZE_MAP[size])}px`,
    }),
    ...(size === "medium" && {
      minHeight: theme.button.size.default.medium,
      padding: theme.spacing(1, 2),
      ...theme.typography.button2,
      lineHeight: `${getIconSize(theme, ICON_SIZE_MAP[size])}px`,
    }),
    ...(size === "large" && {
      minHeight: theme.button.size.default.large,
      padding: theme.spacing(1, 2),
      ...theme.typography.button1,
      lineHeight: `${getIconSize(theme, ICON_SIZE_MAP[size])}px`,
    }),
  };
};

export const getFilledSx = (theme: Theme, vibeKey: FilledButtonVibe): SxProps<Theme> => {
  const surfaceVibe =
    vibeKey === "alternate" ? theme.palette.surface.alternate : theme.palette.surface[vibeKey].intense;

  const textVibe = vibeKey === "alternate" ? theme.palette.text.alternate : theme.palette.text.reverse.intense;

  return {
    backgroundColor: surfaceVibe,
    color: textVibe,
    ["&:hover"]: {
      backgroundColor: darken(surfaceVibe, 0.3),
    },
  };
};

export const getTonalSx = (theme: Theme, vibeKey: OtherButtonVibe): SxProps<Theme> => {
  return {
    backgroundColor: theme.palette.surface[vibeKey].moderate,
    color: theme.palette.text[vibeKey].intense,
    ["&:hover"]: {
      backgroundColor: darken(theme.palette.surface[vibeKey].moderate, 0.1),
    },
  };
};

export const getOutlinedSx = (theme: Theme, vibeKey: OtherButtonVibe): SxProps<Theme> => {
  return {
    border: `1px solid ${theme.palette.border[vibeKey].moderate}`,
    color: theme.palette.text[vibeKey].intense,
    ["&:hover"]: {
      backgroundColor: theme.palette.surface[vibeKey].quiet,
    },
  };
};

export const getTextSx = (theme: Theme, vibeKey: OtherButtonVibe): SxProps<Theme> => {
  return {
    color: theme.palette.text[vibeKey].moderate,
    ["&:hover"]: {
      backgroundColor: theme.palette.surface[vibeKey].quiet,
      color: theme.palette.text[vibeKey].intense,
    },
  };
};
